import { useState, useRef, useEffect } from "react";
import { FormGroup, Label, Input, FormFeedback, Row, Col } from "reactstrap";
import { Scissors, RefreshCcw, Trash2 } from "lucide-react";
import { SUPPORTED_FORMATS, MAX_IMAGE_SIZE } from "../utils/formConfig";
import { showError } from "../components/AppToast"; // 👈 use toast helper
// import "../styles/image-upload.css";

export default function ImageUploadStep({ formik, onOpenCrop }) {
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileMeta, setFileMeta] = useState(null);
  const fileRef = useRef(null);
  const [secondaryPreview, setSecondaryPreview] = useState(null);
  const [secondaryFileMeta, setSecondaryFileMeta] = useState(null);

  const secondaryFileRef = useRef(null);

  // 🔹 Helper: convert File → Base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };
  const handleSecondaryFile = async (file) => {
    if (!file) return;

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      showError("Invalid file type. Please upload PNG, JPG, WEBP, or SVG.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      showError("Image size must be less than 2MB");
      return;
    }

    formik.setFieldValue(
      "secondaryLogo",
      file
    );

    formik.setFieldTouched(
      "secondaryLogo",
      true,
      false
    );

    const previewUrl =
      URL.createObjectURL(file);

    setSecondaryPreview(previewUrl);

    setSecondaryFileMeta({
      name: file.name,
      size:
        (file.size / 1024).toFixed(1) +
        " KB",
    });

    try {
      const base64 = await fileToBase64(file);
      sessionStorage.setItem("secondaryLogo", base64);
    } catch (err) {
      showError("Failed to process image");
    }
  };
 const removeSecondaryLogo = () => {
  formik.setFieldValue(
    "secondaryLogo",
    null
  );

  formik.setFieldError(
    "secondaryLogo",
    undefined
  );

  formik.setFieldTouched(
    "secondaryLogo",
    false
  );

  setSecondaryPreview(null);
  setSecondaryFileMeta(null);

  sessionStorage.removeItem(
    "secondaryLogo"
  );
};
  // 🔹 Handle file upload
  const handleFile = async (file) => {
    if (!file) return;

    setPreview(null);
    setFileMeta(null);

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      formik.setFieldError("avatar", "Invalid file type");
      showError("Invalid file type. Please upload PNG, JPG, WEBP, or SVG.");
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      // formik.setFieldError("avatar", "File size must be less than 2MB");
      showError("Image size must be less than 2MB"); // 👈 toast error
      return;
    }

    formik.setFieldValue("avatar", file);
    formik.setFieldTouched("avatar", true, false);

    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);
    setFileMeta({
      name: file.name,
      size: (file.size / 1024).toFixed(1) + " KB",
    });

    try {
      const base64 = await fileToBase64(file);
      sessionStorage.setItem("avatar", base64);
    } catch (err) {
      console.error("Failed to convert file:", err);
      showError("Failed to process image");
    }
  };

  // 🔹 Remove image
  const removeImage = () => {
    formik.setFieldValue("avatar", null);
    formik.setFieldError("avatar", undefined);
    formik.setFieldTouched("avatar", false);

    setPreview(null);
    setFileMeta(null);

    sessionStorage.removeItem("avatar");
  };

  // 🔹 Restore image from sessionStorage on mount
  useEffect(() => {
    const storedImage = sessionStorage.getItem("avatar");

    if (storedImage) {
      setPreview(storedImage);
      setFileMeta({
        name: "Stored Image",
        size: "",
      });

      formik.setFieldValue("avatar", storedImage);
    }

    const storedSecondaryLogo =
      sessionStorage.getItem("secondaryLogo");

    if (storedSecondaryLogo) {
      setSecondaryPreview(
        storedSecondaryLogo
      );

      setSecondaryFileMeta({
        name: "Stored Image",
        size: "",
      });

      formik.setFieldValue(
        "secondaryLogo",
        storedSecondaryLogo
      );
    }
  }, []);

  return (
    <FormGroup>
      <span className="text-muted small d-block mb-3">
    Note : Logo upload is optional. You can upload one logo, two logos, or no logo. If no logo is uploaded, the form title initials will be displayed automatically.
</span>

      <Row  >
  {/* <Col md="6"> */}
<Col md="6">
        {/* <h6>Primary Logo</h6> */}
      <div
        className={`px-dropzone ${dragOver ? "drag-over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files[0]);
        }}
        onClick={() => !preview && fileRef.current.click()}
      >
        <input
          ref={fileRef}
          hidden
          type="file"
          accept="image/*"
          onChange={(e) => {
            handleFile(e.target.files[0]);
            e.target.value = ""; // reset so same file can be reselected
          }}
        />

        {/* EMPTY STATE */}
        {!preview && (
          <div className="px-drop-empty">
            <div className="px-drop-title ">Drop image here</div>
            <div className="px-drop-sub">or click to browse</div>
          </div>
        )}

        {/* PREVIEW STATE */}
        {preview && (
          <div className="px-avatar-preview">
            <img src={preview} alt="Preview" />

            <div className="px-file-meta">
              <strong>{fileMeta?.name}</strong>
              <span>{fileMeta?.size}</span>
            </div>

            <div className="px-avatar-actions modern">
              {/* CROP */}
              {/* <button
                type="button"
                className="px-icon-btn"
                data-tooltip="Crop image"
                onClick={() => onOpenCrop(preview)}
              >
                <Scissors size={16} />
              </button> */}

              {/* REPLACE */}
              <button
                type="button"
                className="px-icon-btn"
                data-tooltip="Replace image"
                onClick={() => fileRef.current.click()}
              >
                <RefreshCcw size={16} />
              </button>

              {/* REMOVE */}
              <button
                type="button"
                className="px-icon-btn danger"
                data-tooltip="Remove image"
                onClick={removeImage}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

        )}
      </div>
      </Col>

<Col md="6">
        {/* <h6>Secondary Logo</h6> */}

        <div
          className={`px-dropzone ${dragOver ? "drag-over" : ""}`}
          onClick={() =>
            !secondaryPreview &&
            secondaryFileRef.current?.click()
          }
        >
          <input
            ref={secondaryFileRef}
            hidden
            type="file"
            accept="image/*"
            onChange={(e) => {
              handleSecondaryFile(e.target.files[0]);
              e.target.value = "";
            }}
          />

          {!secondaryPreview && (
            <div className="px-drop-empty">
              <div className="px-drop-title">
                Drop image here
              </div>

              <div className="px-drop-sub">
                or click to browse
              </div>
            </div>
          )}

       {secondaryPreview && (
  <div className="px-avatar-preview">
 <img src={secondaryPreview} alt="Preview" />

    <div className="px-file-meta">
      <strong>
        {secondaryFileMeta?.name}
      </strong>

      <span>
        {secondaryFileMeta?.size}
      </span>
    </div>

    <div className="px-avatar-actions modern">
      <button
        type="button"
        className="px-icon-btn"
        data-tooltip="Replace image"
        onClick={() =>
          secondaryFileRef.current?.click()
        }
      >
        <RefreshCcw size={16} />
      </button>

      <button
        type="button"
        className="px-icon-btn danger"
        data-tooltip="Remove image"
        onClick={removeSecondaryLogo}
      >
        <Trash2 size={16} />
      </button>
    </div>
  </div>
)}
        </div>

        {/* <div className="px-upload-hint">
          Secondary Logo • PNG, JPG, WEBP, SVG • Max 2MB
        </div> */}
        </Col>
</Row>
      {/* </div> */}


      {/* ERROR */}
      {formik.touched.avatar && formik.errors.avatar && (
        <FormFeedback className="d-block">{formik.errors.avatar}</FormFeedback>
      )}

      {/* HINT */}
      <div className="px-upload-hint">
        Supported formats: PNG, JPG, WEBP, SVG · Max size: 2MB
      </div>
    </FormGroup>
  );
}

